import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getProduct } from "@/lib/adapters/products";

const WishlistContext = createContext(null);

const LS_KEY = "dyntra_wishlist_v1";

function readLocal() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocal(ids) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(ids));
  } catch { /* ignore quota */ }
}

export function WishlistProvider({ children }) {
  const [ids, setIds] = useState([]);
  const [products, setProducts] = useState({});
  const idsRef = useRef([]);

  useEffect(() => { idsRef.current = ids; }, [ids]);

  useEffect(() => {
    const initial = readLocal();
    idsRef.current = initial;
    setIds(initial);
  }, []);

  const hydrate = useCallback(async (idList) => {
    const missing = idList.filter((id) => !products[id]);
    if (missing.length === 0) return;
    const results = await Promise.all(
      missing.map((id) => getProduct(id).catch(() => null))
    );
    const next = { ...products };
    results.forEach((p, i) => {
      if (p) next[missing[i]] = p;
    });
    setProducts(next);
  }, [products]);

  useEffect(() => {
    if (ids.length) hydrate(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  const isInWishlist = useCallback((productId) => ids.includes(productId), [ids]);

  const toggleWishlist = useCallback((product) => {
    if (!product || !product.id) return;
    const wasIn = idsRef.current.includes(product.id);
    const next = wasIn
      ? idsRef.current.filter((x) => x !== product.id)
      : [...idsRef.current, product.id];
    idsRef.current = next;
    writeLocal(next);
    setIds(next);
    if (!wasIn) setProducts((pc) => ({ ...pc, [product.id]: product }));
    toast.success(wasIn ? "Removed from wishlist" : "Added to wishlist");
  }, []);

  const removeFromWishlist = useCallback((productId) => {
    if (!idsRef.current.includes(productId)) return;
    const next = idsRef.current.filter((x) => x !== productId);
    idsRef.current = next;
    writeLocal(next);
    setIds(next);
    toast.success("Removed from wishlist");
  }, []);

  const clearWishlist = useCallback(() => {
    idsRef.current = [];
    writeLocal([]);
    setIds([]);
  }, []);

  const items = ids
    .map((id) => products[id])
    .filter(Boolean);

  return (
    <WishlistContext.Provider
      value={{
        ids,
        items,
        count: ids.length,
        isInWishlist,
        toggleWishlist,
        removeFromWishlist,
        clearWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx) {
    // Safe default so components rendered outside provider don't crash
    return {
      ids: [],
      items: [],
      count: 0,
      isInWishlist: () => false,
      toggleWishlist: () => {},
      removeFromWishlist: () => {},
      clearWishlist: () => {},
    };
  }
  return ctx;
}
